/* iter196 — Field Notes Review page (/admin/field-notes).
   ------------------------------------------------------------
   Central place for office/admin to see every note a field tech submitted
   during job completion — WITHOUT having to open every invoice. Complements
   /admin/followups (workflow queue) by surfacing:
     - Recommended follow-ups (also shown in /admin/followups)
     - Tech payment notes ("Paid — Zelle, sent to Adam Cordle")
     - HSP / warranty notes
     - Customer-not-satisfied signals
     - Return-visit-needed / needs-estimate flags
     - Opportunity flags
     - Photos
   Filters make it easy for office to only look at "unreviewed" or a specific
   signal category. Rows can be marked "Reviewed" so unreviewed drives the
   sidebar badge.
   ------------------------------------------------------------ */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatInvoiceStatus } from '../../lib/paymentFmt';
import {
  ClipboardList, User, Calendar, Clock, Search, CheckCircle2, XCircle,
  ArrowRight, MessageSquare, DollarSign, ShieldAlert, Camera, Star, Undo2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import FieldCompletionDetails, { CompletionFlagBadges } from '../../components/FieldCompletionDetails';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = [
  { k: 'unreviewed',              l: 'Unreviewed',          icon: ClipboardList },
  { k: 'follow_up',               l: 'Follow-Up',           icon: MessageSquare },
  { k: 'payment_notes',           l: 'Payment Notes',       icon: DollarSign },
  { k: 'hsp_warranty',            l: 'HSP / Warranty',      icon: ShieldAlert },
  { k: 'customer_not_satisfied',  l: 'Not Satisfied',       icon: Star },
  { k: 'return_visit',            l: 'Return Visit',        icon: ArrowRight },
  { k: 'needs_estimate',          l: 'Needs Estimate',      icon: ArrowRight },
  { k: 'office_review',           l: 'Office Review',       icon: ClipboardList },
  { k: 'has_photos',              l: 'Photos',              icon: Camera },
  { k: 'all',                     l: 'All',                 icon: ClipboardList },
];

const fmtDT = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const AdminFieldNotes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('unreviewed');
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState({ open: false, row: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('filter', filter);
      if (q.trim()) params.set('q', q.trim());
      const { data } = await axios.get(`${API}/admin/field-notes?${params.toString()}`, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
    setLoading(false);
  }, [filter, q]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDrawer = (row) => setDrawer({ open: true, row });
  const closeDrawer = () => setDrawer({ open: false, row: null });

  const markReviewed = async (row, evt) => {
    if (evt) evt.stopPropagation();
    try {
      const { data } = await axios.put(`${API}/admin/field-notes/${row.completion_id}/review`, {}, { withCredentials: true });
      toast.success('Marked reviewed');
      setRows((rs) => rs.map((r) => r.completion_id === row.completion_id ? { ...r, field_notes_reviewed_at: data.reviewed_at } : r));
      // If we're on unreviewed filter, remove it.
      if (filter === 'unreviewed') {
        setTimeout(() => setRows((rs) => rs.filter((r) => r.completion_id !== row.completion_id)), 400);
      }
      if (drawer.open && drawer.row?.completion_id === row.completion_id) {
        setDrawer({ open: true, row: { ...drawer.row, field_notes_reviewed_at: data.reviewed_at } });
      }
    } catch { toast.error('Failed'); }
  };

  const undoReview = async (row, evt) => {
    if (evt) evt.stopPropagation();
    try {
      await axios.delete(`${API}/admin/field-notes/${row.completion_id}/review`, { withCredentials: true });
      toast.success('Review undone');
      setRows((rs) => rs.map((r) => r.completion_id === row.completion_id ? { ...r, field_notes_reviewed_at: null } : r));
      if (drawer.open && drawer.row?.completion_id === row.completion_id) {
        setDrawer({ open: true, row: { ...drawer.row, field_notes_reviewed_at: null } });
      }
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-field-notes-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-[#D4AF37]" /> Field Notes Review
        </h2>
        <p className="text-[#C9C9C9] text-sm">
          Every note the field techs submit — follow-ups, payment notes, HSP notes, customer concerns —
          in one place so nothing gets lost inside an invoice drawer.
        </p>
      </div>

      {/* Filter strip */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <Button
            key={f.k}
            data-testid={`field-notes-filter-${f.k}`}
            size="sm"
            onClick={() => setFilter(f.k)}
            variant={filter === f.k ? 'default' : 'outline'}
            className={filter === f.k
              ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold'
              : 'border-[#262626] text-white hover:bg-[#262626]'}
          >
            <f.icon className="w-3.5 h-3.5 mr-1" /> {f.l}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none" />
          <Input
            data-testid="field-notes-search"
            type="text"
            placeholder="Search customer / tech / notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white pl-8 h-9"
          />
        </div>
      </div>

      {loading && <p className="text-[#A3A3A3] text-sm">Loading…</p>}
      {!loading && rows.length === 0 && (
        <div data-testid="field-notes-empty" className="bg-[#141414] border border-[#262626] rounded p-6 text-center text-[#A3A3A3] text-sm">
          No field notes in this view.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.completion_id}
            data-testid={`field-note-row-${r.completion_id}`}
            className={`border rounded p-3 cursor-pointer hover:bg-[#1a1a1a] ${r.field_notes_reviewed_at ? 'bg-[#141414] border-[#262626]' : 'bg-[#141414] border-[#D4AF37]/30'}`}
            onClick={() => openDrawer(r)}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <User className="w-4 h-4 text-[#D4AF37]" />
                  <p className="text-white font-bold text-sm">{r.customer_name || 'Customer'}</p>
                  {r.invoice_status && (
                    <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#262626] px-1.5 py-0.5 rounded">
                      {formatInvoiceStatus(r.invoice_status)}
                    </span>
                  )}
                  {r.field_notes_reviewed_at && (
                    <span data-testid={`field-note-reviewed-${r.completion_id}`} className="text-[10px] uppercase tracking-wider text-green-300 bg-green-500/10 border border-green-500/40 px-1.5 py-0.5 rounded flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Reviewed
                    </span>
                  )}
                </div>
                <div className="mt-1.5">
                  <CompletionFlagBadges flags={r.flags} testidPrefix={`field-note-${r.completion_id}`} />
                </div>
                {r.recommended_followups && (
                  <div className="mt-2 text-sm bg-[#1a1505] border-l-2 border-[#D4AF37]/60 pl-2 py-1 rounded">
                    <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-bold mb-0.5">Follow-Up</p>
                    <p className="text-white whitespace-pre-wrap line-clamp-2" data-testid={`field-note-followup-${r.completion_id}`}>{r.recommended_followups}</p>
                  </div>
                )}
                {r.payment_notes && (
                  <div className="mt-2 text-sm bg-[#0A0A0A] border-l-2 border-[#D4AF37]/40 pl-2 py-1 rounded">
                    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider font-bold mb-0.5">Payment Notes</p>
                    <p className="text-white whitespace-pre-wrap line-clamp-2">{r.payment_notes}</p>
                  </div>
                )}
                {r.hsp_notes && (
                  <div className="mt-2 text-sm bg-[#0A0A0A] border-l-2 border-blue-400/50 pl-2 py-1 rounded">
                    <p className="text-blue-300 text-[10px] uppercase tracking-wider font-bold mb-0.5">HSP / Warranty Notes</p>
                    <p className="text-white whitespace-pre-wrap line-clamp-2">{r.hsp_notes}</p>
                  </div>
                )}
                {r.major_concern && (
                  <div className="mt-2 text-sm bg-[#0A0A0A] border-l-2 border-amber-500/40 pl-2 py-1 rounded">
                    <p className="text-amber-300 text-[10px] uppercase tracking-wider font-bold mb-0.5">Major Concern</p>
                    <p className="text-white whitespace-pre-wrap line-clamp-2">{r.major_concern}</p>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-[#A3A3A3] flex-wrap">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.employee_name || 'tech'}</span>
                  {r.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.date}</span>}
                  {r.submitted_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDT(r.submitted_at)}</span>}
                  {r.shift_title && <span className="text-[#525252]">{r.shift_title}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {r.field_notes_reviewed_at ? (
                  <Button
                    data-testid={`field-note-undo-${r.completion_id}`}
                    size="sm"
                    variant="outline"
                    onClick={(e) => undoReview(r, e)}
                    className="border-[#262626] text-[#A3A3A3] hover:bg-[#262626] h-8"
                  >
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo
                  </Button>
                ) : (
                  <Button
                    data-testid={`field-note-review-${r.completion_id}`}
                    size="sm"
                    onClick={(e) => markReviewed(r, e)}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-8"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Reviewed
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detail drawer */}
      <Sheet open={drawer.open} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <SheetContent side="right" data-testid="field-note-drawer" className="bg-[#0A0A0A] border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[#D4AF37] flex items-center gap-2">
              <ClipboardList className="w-4 h-4" /> Field Notes · {drawer.row?.customer_name || ''}
            </SheetTitle>
          </SheetHeader>
          {drawer.row && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {drawer.row.field_notes_reviewed_at ? (
                  <Button
                    data-testid="field-note-drawer-undo"
                    size="sm"
                    variant="outline"
                    onClick={(e) => undoReview(drawer.row, e)}
                    className="border-[#262626] text-white hover:bg-[#262626]"
                  >
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Review
                  </Button>
                ) : (
                  <Button
                    data-testid="field-note-drawer-review"
                    size="sm"
                    onClick={(e) => markReviewed(drawer.row, e)}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Reviewed
                  </Button>
                )}
                {drawer.row.customer_id && (
                  <Link to={`/admin/customers/${drawer.row.customer_id}`}>
                    <Button variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                      Open Customer <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </Link>
                )}
                {drawer.row.invoice_id && (
                  <Link to={`/admin/invoices?open=${drawer.row.invoice_id}`}>
                    <Button variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                      Open Invoice <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
              <FieldCompletionDetails completionId={drawer.row.completion_id} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminFieldNotes;
